import React__default, { forwardRef, useRef, useMemo, useImperativeHandle, useEffect } from 'react';
import { Slate, ReactEditor } from 'slate-react';
import { useDragDrop } from '../../editor/plugins/abstract/drag-and-drop/useDragDrop.mjs';
import { useFocus } from '../../editor/plugins/abstract/focus/useFocus.mjs';
import { usePopover } from '../../editor/plugins/shared/components/Popover/usePopover.mjs';
import { Editable } from '../../core/components/Editable/Editable.mjs';
import { useDerivedSelection } from '../../editor/plugins/abstract/selection/useDerivedSelection.mjs';
import { PopoverContext } from '../../editor/plugins/shared/components/Popover/Popover.mjs';

const Editor = /*#__PURE__*/forwardRef(function Editor({
  editor,
  onChange,
  onSelectionChange,
  plugins,
  renderEditor,
  selection,
  value,
  ...props
}, ref) {
  const {
    isDragging,
    handlers: dragDropHandlers
  } = useDragDrop(editor, {
    plugins
  });
  const {
    handlers: focusHandlers
  } = useFocus(editor);
  const previousSelection = useRef(selection);
  const previousValue = useRef(value);
  const popover = usePopover();
  const {
    showPopovers,
    rerenderPopovers
  } = popover;
  const popoverContext = useMemo(() => ({
    ...popover,
    globalPopoverVisibility: isDragging ? false : popover.globalPopoverVisibility
  }), [popover, isDragging]);
  useImperativeHandle(ref, () => ({
    rerenderPopovers
  }));
  const editableMarkup = /*#__PURE__*/React__default.createElement(Editable, Object.assign({
    plugins: plugins
  }, dragDropHandlers, focusHandlers, props));

  // Refer to https://github.com/ianstormtaylor/slate/issues/4612
  if (value !== previousValue.current) {
    editor.children = value;
    previousValue.current = value;
  }
  useDerivedSelection(editor, {
    value: selection,
    onChange: onSelectionChange
  });
  useEffect(() => {
    if (selection !== previousSelection.current) {
      showPopovers();
      previousSelection.current = selection;
    }
  }, [selection, showPopovers]);
  return /*#__PURE__*/React__default.createElement(PopoverContext.Provider, {
    value: popoverContext
  }, /*#__PURE__*/React__default.createElement(Slate, {
    editor: editor,
    value: value,
    onChange: onChange
  }, renderEditor ? renderEditor(editableMarkup, {
    focused: ReactEditor.isFocused(editor)
  }) : editableMarkup));
});

export { Editor };
